// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import {
    PrimaryButton,
    TooltipHost,
} from 'office-ui-fabric-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import AddressDetails from './../../components/AddressDetails';
import ClaimOrder from './../../components/ClaimOrder';
import CommonLayout from './../../components/CommonLayout';
import { logPageAction, logException } from 'src/lib/ApplicationInsights';
import CorrelationVector from './../../lib/CorrelationVector';
import ErrorMessage from './../../components/ErrorMessage/ErrorMessage';
import { OrderSummary } from './../../components/Order';
import { statusSelector } from './../../components/ServiceRequestAndDeviceLookup/selector';
import { selectedCountryInfoSelector } from './../../store/countries/selectors';

import history from 'src/history';
import {
    IClaimOrder,
} from './../../businessLogic/sagas/serviceRequest/types';
import { LoadingState } from './../../store/actionStatus';
import {
    claimsSelector, deviceSelector,
} from './../../store/deviceInfo/selectors';

import tooltipIcon from './../../assets/tooltip-copy-3@2x.png';
import {
    PURCHASE_ORDER,
    PURCHASE_ON_DEMAND_ORDER,
    reActivateOrderStatusSelector,
    secondSRCreationSelector
} from './../../store/orderCreation';

import Styles from './orderConfirmation.module.scss';
import { userInfoSelector } from './../../store/user';
import auditLogEvents from './../../constants/auditLogEvents';

const exceptionMessageForPageNavigation = 'Somehow the order confirmation page was reached without a order id or claim order id';
const exceptionMessageForNoConfirmationId = 'The order confirmation page was reached without a claimOrderId';

function Address({ claimOrder }: { claimOrder: IClaimOrder }) {
    const { t } = useTranslation();

    return (
        <div className={Styles.address}>
            <h2 className={Styles.detailSection}>{t('address_details')}</h2>
            <AddressDetail claimOrder={claimOrder} />
        </div>
    );
}

function AddressDetail({ claimOrder }: { claimOrder: IClaimOrder }) {
    return (
        <AddressDetails data={claimOrder} />
    );
}

function CloseButton() {
    const { t } = useTranslation();

    return (
        <PrimaryButton
            autoFocus={true}
            className={Styles.close}
            text={t('close')}
            onClick={onClose}
        />
    );
}

function Details({ claimOrder }: { claimOrder: IClaimOrder }) {
    const { t } = useTranslation();
    return (
        <div>
            <h2 className={Styles.detailSection}>{t('order_details')}</h2>
            <ClaimOrder data={claimOrder} />
        </div>
    );
}

function isServiceRequestResponseParsed(data: any): data is IClaimOrder {
    return data && 'claimOrderId' in data
        && Array.isArray(data.serviceRequests);
}

function onClose() {
    logPageAction('Close button clicked');
    history.push('/');
}

export default function OrderDetailPage() {
    const { t } = useTranslation();
    const reActivateOrder = useSelector(state => reActivateOrderStatusSelector(state));
    const secondSRCreation = useSelector(state => secondSRCreationSelector(state));
    let orderStatus;
    if(reActivateOrder || secondSRCreation){
        orderStatus = useSelector(state => statusSelector(state)(PURCHASE_ON_DEMAND_ORDER));
    } else {
        orderStatus = useSelector(state => statusSelector(state)(PURCHASE_ORDER));
    }

    return (
        <CommonLayout
            loading={orderStatus.status === LoadingState.started}
            loadingMessage={t('finalize_order')}
            pageTitle={[t('order_confirmation')]}
        >
            <Page />
        </CommonLayout>);
}

function OrderDetail({ claimOrder }: { claimOrder: IClaimOrder }) {
    const { t } = useTranslation();
    const payment = claimOrder.payments || {};
    const billTo = payment.billTo || {};
    const lineItem = claimOrder.lineItems[0] || {};
    const { deviceInfo } = lineItem;

    const countryInfo = useSelector(state => selectedCountryInfoSelector(state));
  
    return (
        <div className={Styles.orderDetails}>
            <div className={Styles.orderDetail}>
                <div className={Styles.orderDetailLabel}>{t('serial_number')}:</div>
                <div className={Styles.orderDetailValue}>{deviceInfo.serialNumber}</div>
            </div>
            <div className={Styles.orderDetail}>
                <div className={Styles.orderDetailLabel}>{t('country')}/{t('region')}:</div>
                <div className={Styles.orderDetailValue}>{ countryInfo && countryInfo.countryName||billTo.country}</div>
            </div>
            <div className={Styles.orderDetail}>
                <div className={Styles.orderDetailLabel}>{t('zip_code')}:</div>
                <div className={Styles.orderDetailValue}>{billTo.postalCode}</div>
                <TooltipHost
                    aria-label={t('tooltip')} // TODO: find out about content of serialNumber tooltip from Keith and Julie ( also Ln #100 )
                    content={t('zip_code')}
                    id="order_zip_code"
                >
                    <img
                        alt={t('zip_code')}
                        aria-label={t('zip_code')}
                        className={Styles.faq}
                        src={tooltipIcon}
                        tabIndex={0}
                        aria-describedby="order_zip_code"
                    />
                </TooltipHost>
            </div>
        </div>
    );
}

function Page() {
    const { t } = useTranslation();
    const reActivateOrder = useSelector(state => reActivateOrderStatusSelector(state));
    const secondSRCreation = useSelector(state => secondSRCreationSelector(state));
    
    const orderId: string = useSelector(
        (state: any): string => state.order && state.order.confirmation && state.order.confirmation.claimOrderId as string
    ) || '';

    let orderStatus;
    if(reActivateOrder || secondSRCreation){
        orderStatus = useSelector(state => statusSelector(state)(PURCHASE_ON_DEMAND_ORDER)) || {};
    } else {
        orderStatus =  useSelector(state => statusSelector(state)(PURCHASE_ORDER)) || {};
    }

    const claimOrders = useSelector((state: any) => claimsSelector(state));
    const claimOrder: IClaimOrder = Array.isArray(claimOrders) && claimOrders.length && claimOrders[0] || {};

    const device = useSelector(state => deviceSelector(state));
    const serialNumber = device && device.serialNumber;

    const traceDataForError = {
        claimOrders,
        orderId,
        orderStatus,
    };

    const correlationVector = CorrelationVector.getCV();
    const hasOrderId = !!orderId;
    const orderFailed = !orderStatus || orderStatus.status === LoadingState.rejected;
    const noValidClaimOrder = !claimOrder.claimOrderId || !isServiceRequestResponseParsed(claimOrder);
    const isInError = orderFailed || noValidClaimOrder;
    let errorMessage = '';

    const user = useSelector(state => userInfoSelector(state));
    const status = isInError ? 'failure' : 'success';
    logPageAction(auditLogEvents.OrderCreationEvent, { serialNumber, orderStatus: status, userId: user.retailUserId});

    if (isInError) {
        if (!hasOrderId) {
            if (!serialNumber) {
                // Reached page via deep link or via refresh or some other way with dumping the redux store
                // Just redirect home, don't need to throw any error, this is the same as the other pages
                history.push('/');
            } else {
                // Got to the page in an invalid state - should never happen
                errorMessage = 'no_order_or_claim_order_id_redirecting';
                logException(correlationVector, exceptionMessageForPageNavigation, traceDataForError, true);
            }
        } else {
            errorMessage = orderStatus.error && orderStatus.error.message || 'finalizing_order_failed';
            logException(correlationVector, exceptionMessageForNoConfirmationId, traceDataForError, true);
        }
    }

    return (
        <div className={Styles.pageBody}>
            <ErrorMessage
                contactSupport={isInError}
                message={t(errorMessage)}
                traceData={traceDataForError}
            />
            {!isInError && orderStatus.status === LoadingState.success ?
                <>
                    <RenderDeviceDetails />
                    <Details claimOrder={claimOrder} />
                    <Address claimOrder={claimOrder} />
                </>
                : null}
            <CloseButton />
        </div>
    );
}

function RenderDeviceDetails() {
    const { t } = useTranslation();

    const claimOrder = useSelector((state: any) => claimsSelector(state))[0];
    return (
        <>
            <div className={Styles.pageType}>
                <h1 className={Styles.pageTitle}>{t('order_confirmation')}</h1>
                <OrderDetail claimOrder={claimOrder} />
                <OrderSummary confirmation={claimOrder} />
            </div>
        </>
    );
}
